import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import axiosInstance from "../axiosInterceptor";
import { Autocomplete, TextField } from "@mui/material";


const AddScheduleNotification = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const [countryList, setCountryList] = useState([]);
  const [selectedCountries, setSelectedCountries] = useState([]);
  const [actionType, setActionType] = useState("Send"); // default Send

  const defaultButtons = [
    { id: "btn_1", label: "", link: "", discountCode: "" },
    { id: "btn_2", label: "", link: "", discountCode: "" },
  ];

  const [form, setForm] = useState({
    title: "",
    body: "",
    image_url: "",
    link: "",
    scheduleDate: "",
    buttons: defaultButtons.map((b) => ({
      id: b.id,
      label: "",
      link: "",
      discountCode: "",
    })),
  });

  const [errors, setErrors] = useState({
    title: "",
    body: "",
    image_url: "",
    link: "",
    scheduleDate: "",
    buttons: "",
  });

  const validateForm = () => {
    const newErrors = {};

    if (!form.title.trim()) {
      newErrors.title = "Title is required";
    }

    if (!form.body.trim()) {
      newErrors.body = "Body is required";
    }

    if (actionType === "Schedule" && !form.scheduleDate) {
      newErrors.scheduleDate = "Schedule date & time is required";
    }

    if (form.image_url.trim() && !/^https?:\/\/.+/i.test(form.image_url)) {
      newErrors.image_url = "Enter a valid image URL";
    }

    const invalidButtonLink = form.buttons.find((b) => {
      if (b.id !== "btn_1") return false; // btn_1 uses link
      const link = b.link?.trim();
      return link && !/^https?:\/\/.+/i.test(link);
    });
    if (invalidButtonLink) newErrors.buttons = "Enter a valid URL for Button 1 link";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const getCountryCodes = async () => {
    try {
      const res = await axiosInstance.get("customer/getCountryCode");

      if (res.data.isSuccess) {
        setCountryList(res.data.data);
      }
    } catch (error) {
      console.error("Country API Error", error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleButtonChange = (index, field, value) => {
    setForm((prev) => ({
      ...prev,
      buttons: prev.buttons.map((b, i) =>
        i === index ? { ...b, [field]: value } : b
      ),
    }));
    if (errors.buttons) {
      setErrors((prev) => ({ ...prev, buttons: "" }));
    }
  };

  const getButtonIndex = (id) => form.buttons.findIndex((b) => b.id === id);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      let scheduleDateISO;
      if (actionType === "Schedule" && form.scheduleDate) {
        // Convert datetime-local (local time) to ISO string
        scheduleDateISO = new Date(form.scheduleDate).toISOString();
      }

      const payload = {
        title: form.title.trim(),
        body: form.body.trim(),
        image_url: form.image_url.trim() || null,
        link: form.link.trim() || null,
        scheduleDate: actionType === "Schedule" ? scheduleDateISO : null,
        countryCode: selectedCountries.includes("all")
          ? null
          : selectedCountries.length
          ? selectedCountries
          : null,
        buttons: form.buttons.map((b) => {
          const id = b.id;
          const label = b.label?.trim() || "";
          if (id === "btn_2") {
            return {
              id,
              label,
              discountCode: b.discountCode?.trim() || "",
            };
          }
          return {
            id,
            label,
            link: b.link?.trim() || "",
          };
        }),
        action: actionType,
      };

      const response = await axiosInstance.post(
        "scheduleNotification/create",
        payload
      );

      const { isSuccess, message } = response.data;

      if (isSuccess) {
        toast.success(message || "Schedule notification saved successfully!");
        navigate("/schedule-notification");
      } else {
        toast.error(message || "Failed to save schedule notification");
      }
    } catch (error) {
      const errorMessage =
        error?.response?.data?.message ||
        "Something went wrong while saving!";
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleActionChange = (value) => {
    setActionType(value);

    if (value === "Send") {
      setErrors((prev) => ({ ...prev, scheduleDate: "" }));
      setForm((prev) => ({
        ...prev,
        scheduleDate: "",
      }));
    }
  };

  useEffect(() => {
    getCountryCodes()
  }, []);

  return (
    <>
      <div className="content-wrapper pd-top">
        <div className="page-header mt-5 mx-4">
          <h3 className="page-title">Add Schedule Notification</h3>
        </div>
        <div className="col-md-12 grid-margin stretch-card">
          <div className="col-md-12">
            <div className="card">
              <div className="card-body">
                <form onSubmit={handleSubmit}>
                  {/* Action Type */}
                  <div className="row mb-4 align-items-end">

                    {/* Action Label */}
                    <div className="col-sm-12 mb-2">
                      <label className="fw-bold">Action</label>
                    </div>

                    {/* Send Notification */}
                    <div className="col-sm-3">
                      <div className="form-check">
                        <input
                          type="radio"
                          className="form-check-input"
                          name="actionType"
                          id="send"
                          value="Send"
                          checked={actionType === "Send"}
                          onChange={() => handleActionChange("Send")}
                        />
                        <label className="form-check-label" htmlFor="send">
                          Send Message
                        </label>
                      </div>
                    </div>

                    {/* Schedule Notification */}
                    <div className="col-sm-3">
                      <div className="form-check">
                        <input
                          type="radio"
                          className="form-check-input"
                          name="actionType"
                          id="schedule"
                          value="Schedule"
                          checked={actionType === "Schedule"}
                          onChange={() => handleActionChange("Schedule")}
                        />
                        <label className="form-check-label" htmlFor="schedule">
                          Schedule Notification
                        </label>
                      </div>
                    </div>

                    {/* Schedule Date Field */}
                    {actionType === "Schedule" && (
                      <div className="col-sm-6">
                        <div className="form-group">
                          <label className="col-form-label">
                            Schedule Date & Time <span className="text-danger">*</span>
                          </label>

                          <input
                            type="datetime-local"
                            className={`form-control form-control-sm ${errors.scheduleDate ? "is-invalid" : ""
                              }`}
                            name="scheduleDate"
                            value={form.scheduleDate}
                            onChange={handleChange}
                          />
                          {errors.scheduleDate && (
                            <div className="invalid-feedback">
                              {errors.scheduleDate}
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                  </div>

                  <div className="row">
                    {/* Title */}
                    <div className="col-sm-6">
                      <div className="form-group">
                        <label className="col-form-label">
                          Notification Title <span className="text-danger">*</span>
                        </label>
                        {/* <input
                          type="text"
                          className={`form-control form-control-sm ${errors.title ? "is-invalid" : ""
                            }`}
                          name="title"
                          placeholder="e.g. Big Sale is Live!"
                          value={form.title}
                          onChange={handleChange}
                        /> */}
                        <textarea
                          className={`form-control form-control-sm ${errors.title ? "is-invalid" : ""
                            }`}
                          name="title"
                          placeholder="e.g. Big Sale is Live!"
                          value={form.title}
                          onChange={handleChange}
                          rows={4}
                        />
                        {errors.title && (
                          <div className="invalid-feedback">
                            {errors.title}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Body */}
                    <div className="col-sm-6">
                      <div className="form-group">
                        <label className="col-form-label">
                          Notification Message <span className="text-danger">*</span>
                        </label>
                        <textarea
                          className={`form-control form-control-sm ${errors.body ? "is-invalid" : ""
                            }`}
                          name="body"
                          placeholder="Write your message here..."
                          rows={4}
                          value={form.body}
                          onChange={handleChange}
                        />
                        {errors.body && (
                          <div className="invalid-feedback">
                            {errors.body}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="row">

                    {/* {actionType === "Schedule" && (
                      <div className="col-sm-6">
                        <div className="form-group">
                          <label>Schedule Date & Time</label>

                          <input
                            type="datetime-local"
                            className={`form-control form-control-sm ${errors.scheduleDate ? "is-invalid" : ""}`}
                            name="scheduleDate"
                            value={form.scheduleDate}
                            onChange={handleChange}
                          />

                          {errors.scheduleDate && (
                            <div className="invalid-feedback">
                              {errors.scheduleDate}
                            </div>
                          )}
                        </div>
                      </div>
                    )} */}

                    <div className="col-sm-6">
                      <div className="form-group">
                        <label className="col-form-label">Country Code</label>
                        <Autocomplete
                          multiple
                          size="small"
                          options={[
                            "all",
                            ...countryList.map((c) => c.countryCode),
                          ]}
                          value={selectedCountries}
                          onChange={(_, value) => {
                            if (value.includes("all")) {
                              setSelectedCountries(["all"]);
                            } else {
                              setSelectedCountries(value);
                            }
                          }}
                          getOptionLabel={(option) =>
                            option === "all" ? "All Countries" : option
                          }
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              placeholder="Select Country"
                              size="small"
                            />
                          )}
                          sx={{ width: "100%" }}
                        />
                      </div>
                    </div>

                    {/* Image URL */}
                    <div className="col-sm-6">
                      <div className="form-group">
                        <label className="col-form-label">Image URL</label>
                        <input
                          type="text"
                          className={`form-control form-control-sm ${errors.image_url ? "is-invalid" : ""
                            }`}
                          name="image_url"
                          placeholder="e.g. https://example.com/image.png"
                          value={form.image_url}
                          onChange={handleChange}
                        />
                        {errors.image_url && (
                          <div className="invalid-feedback">
                            {errors.image_url}
                          </div>
                        )}
                        {form.image_url && !errors.image_url && (
                          <div
                            className="mt-2 p-2"
                            style={{
                              borderRadius: "8px",
                              border: "1px solid #e0e0e0",
                              backgroundColor: "#fafafa",
                            }}
                          >
                            <img
                              src={form.image_url}
                              alt="Preview"
                              style={{
                                width: "100%",
                                maxHeight: 220,
                                objectFit: "contain",
                                borderRadius: 8,
                              }}
                              onError={(e) => (e.target.style.display = "none")}
                            />
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Link */}
                    <div className="col-sm-12">
                      <div className="form-group">
                        <label className="col-form-label">Link</label>
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          name="link"
                          placeholder="e.g. https://example-link.com"
                          value={form.link}
                          onChange={handleChange}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="row">
                    <div className="col-sm-12">
                      <div className="form-group">
                        <div className="d-flex align-items-center justify-content-between">
                          <label className="col-form-label fw-bold mb-0">
                            Action Buttons
                          </label>
                          <small className="text-muted">
                            Two fixed buttons: Link + Discount Code
                          </small>
                        </div>

                        {/* Button 1 (Link) */}
                        {(() => {
                          const idx = getButtonIndex("btn_1");
                          const btn = idx >= 0 ? form.buttons[idx] : { id: "btn_1", label: "", link: "" };
                          return (
                            <div className="border rounded p-3 mb-3 bg-white">
                              <div className="d-flex align-items-center justify-content-between mb-2">
                                <div>
                                  <div className="fw-semibold">Button 1 (Link)</div>
                                </div>
                                <span className="badge bg-light text-dark border">{btn.id}</span>
                              </div>

                              <div className="row">
                                <div className="col-md-4">
                                  <label className="col-form-label">Button Label</label>
                                  <input
                                    type="text"
                                    className="form-control form-control-sm"
                                    placeholder="e.g. Shop Now"
                                    value={btn.label}
                                    onChange={(e) => handleButtonChange(idx, "label", e.target.value)}
                                  />
                                </div>
                                <div className="col-md-8">
                                  <label className="col-form-label">Button Link</label>
                                  <input
                                    type="text"
                                    className={`form-control form-control-sm ${errors.buttons ? "is-invalid" : ""}`}
                                    placeholder="e.g. https://sareesbazaar.in/collections/readymade"
                                    value={btn.link || ""}
                                    onChange={(e) => handleButtonChange(idx, "link", e.target.value)}
                                  />
                                </div>
                              </div>
                            </div>
                          );
                        })()}

                        {/* Button 2 (Discount Code) */}
                        {(() => {
                          const idx = getButtonIndex("btn_2");
                          const btn = idx >= 0 ? form.buttons[idx] : { id: "btn_2", label: "", discountCode: "" };
                          return (
                            <div className="border rounded p-3 mb-2 bg-white">
                              <div className="d-flex align-items-center justify-content-between mb-2">
                                <div>
                                  <div className="fw-semibold">Button 2 (Discount Code)</div>
                                </div>
                                <span className="badge bg-light text-dark border">{btn.id}</span>
                              </div>

                              <div className="row">
                                <div className="col-md-4">
                                  <label className="col-form-label">Discount Label</label>
                                  <input
                                    type="text"
                                    className="form-control form-control-sm"
                                    placeholder="e.g. Copy Code"
                                    value={btn.label}
                                    onChange={(e) => handleButtonChange(idx, "label", e.target.value)}
                                  />
                                </div>
                                <div className="col-md-8">
                                  <label className="col-form-label">Discount Code</label>
                                  <input
                                    type="text"
                                    className="form-control form-control-sm"
                                    placeholder="e.g. 10%OFF"
                                    value={btn.discountCode || ""}
                                    onChange={(e) =>
                                      handleButtonChange(idx, "discountCode", e.target.value)
                                    }
                                  />
                                </div>
                              </div>
                            </div>
                          );
                        })()}

                        {errors.buttons && (
                          <div className="invalid-feedback d-block">
                            {errors.buttons}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-sm-12">
                      <div className="text-start mt-4">
                        <button
                          type="submit"
                          className="btn btn-primary mr-2"
                          disabled={loading}
                        >
                          {loading ? (
                            <>
                              <span
                                className="spinner-border spinner-border-sm me-2 mx-2"
                                role="status"
                                aria-hidden="true"
                              ></span>
                              Processing...
                            </>
                          ) : (
                            "Save"
                          )}
                        </button>
                        <Link to="/schedule-notification">
                          <button
                            type="button"
                            className="btn btn-secondary"
                          >
                            Back
                          </button>
                        </Link>
                      </div>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AddScheduleNotification;
